import type { ReactNode } from "react";
import { costRange, fmtUsd, type M3pProjection } from "@dearbill/shared";
import { CostFigure } from "../home/CostFigure";
import { unavailableCopy } from "../home/estimateCopy";

/**
 * The page's headline figures. A priced estimate whose bounds differ shows the
 * low end (deductible already met, Forest) beside the high end (deductible
 * unmet, Deep Navy), each with its deductible caption. Bounds that format
 * alike collapse to one full-width figure with no caption, since there is no
 * range to explain. An unavailable estimate (`m3p` null or `totalOop` null) is
 * one "Still calculating" card with the pending copy, or the not-offered copy
 * for a next-year block whose plan has no next-year snapshot.
 */
export function HeadlineCards({ m3p }: { m3p: M3pProjection | null }): ReactNode {
  if (m3p === null || m3p.totalOop === null) {
    return (
      <HeadlineCard kicker="ESTIMATED" caption={unavailableCopy(m3p)}>
        <CostFigure amount={null} />
      </HeadlineCard>
    );
  }
  const range = costRange(m3p.totalOopMin, m3p.totalOop);
  if (!range.ranged) {
    return (
      <HeadlineCard kicker="ESTIMATED">
        <CostFigure amount={range.text} />
      </HeadlineCard>
    );
  }
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <HeadlineCard
        kicker="LOW END (ESTIMATED)"
        caption="Assumes your Part D deductible is already met."
      >
        <CostFigure amount={fmtUsd(m3p.totalOopMin)} tone="forest" />
      </HeadlineCard>
      <HeadlineCard
        kicker="HIGH END (ESTIMATED)"
        caption="Assumes none of your deductible is met yet."
      >
        <CostFigure amount={fmtUsd(m3p.totalOop)} />
      </HeadlineCard>
    </div>
  );
}

interface HeadlineCardProps {
  kicker: string;
  /** Under the figure; absent on a single figure, which has no deductible bound to explain. */
  caption?: string;
  children: ReactNode;
}

/**
 * The kicker's and caption's classes compose as plain strings, not with cn():
 * tailwind-merge can't tell the custom text-size tokens from the text-* colors
 * and would drop the size token.
 */
function HeadlineCard({ kicker, caption, children }: HeadlineCardProps): ReactNode {
  return (
    <div className="border-haze flex flex-col gap-4 rounded-2xl border bg-white p-7">
      <p className="text-overline text-slate-teal uppercase">{kicker}</p>
      {children}
      {caption !== undefined && <p className="text-body-sm text-slate-teal">{caption}</p>}
    </div>
  );
}
